import { useRouteMatch } from 'react-router';
import { useSelector } from 'react-redux';
import { useState } from 'react';
import { RiskModelSummary } from 'app/modules/riskRatings/responses';
import {
  U21Spacer,
  U21Button,
  U21Typography,
  U21Subsection,
} from 'app/shared/u21-ui/components';
import { IconArrowRight, IconPencil } from '@u21/tabler-icons';
import { RiskScoreOverrideModal } from 'app/modules/riskScore/components/RiskScoreOverrideModal';
import { ClearOverrideConfirmModal } from 'app/modules/riskScore/components/ClearOverrideConfirmModal';
import { ROUTES_MAP } from 'app/shared/utils/routes';
import {
  selectHasOverrideRiskRatingsPermission,
  selectHasReadRiskRatingsPermission,
} from 'app/modules/session/selectors';
import styled from 'styled-components';
import { RiskScoreModelDetails } from 'app/modules/riskScore/components/RiskScoreModelDetails';
import { RiskScoreOverrideDetails } from 'app/modules/riskScore/components/RiskScoreOverrideDetails';
import { EntityLatestRiskScore } from 'app/modules/riskScore/models';

interface RiskScoreEvidenceProps {
  latestRiskScore: EntityLatestRiskScore;
  readOnly?: boolean;
  riskModelSummary: Pick<
    RiskModelSummary,
    'high_risk_floor' | 'id' | 'low_risk_ceiling' | 'title'
  >;
}

export const RiskScoreDetails = ({
  latestRiskScore,
  readOnly,
  riskModelSummary,
}: RiskScoreEvidenceProps) => {
  const { source } = latestRiskScore;

  const [overrideModalOpen, setOverrideModalOpen] = useState(false);
  const [clearOverrideModalOpen, setClearOverrideModalOpen] = useState(false);

  const canReadRiskModel = useSelector(selectHasReadRiskRatingsPermission);

  const hasOverridePermission = useSelector(
    selectHasOverrideRiskRatingsPermission,
  );

  const isOnRiskModelDetails = Boolean(
    useRouteMatch(ROUTES_MAP.riskRatingsId.path),
  );
  const showModelRedirect = !isOnRiskModelDetails && canReadRiskModel;

  return (
    <U21Spacer spacing={2}>
      <StyledEvidenceHeader>
        <U21Typography variant="h6">Risk Score Evidence</U21Typography>
        {!readOnly && hasOverridePermission && (
          <U21Button
            startIcon={<IconPencil />}
            onClick={() => setOverrideModalOpen(true)}
            size="small"
          >
            Override
          </U21Button>
        )}
      </StyledEvidenceHeader>
      <U21Spacer>
        {source === 'MANUAL' && (
          <U21Subsection
            collapsible
            shadedHeader
            title="Override Score Details"
          >
            <RiskScoreOverrideDetails
              latestRiskScore={latestRiskScore}
              onClearOverride={() => setClearOverrideModalOpen(true)}
              readOnly={readOnly}
            />
          </U21Subsection>
        )}
        {source === 'MANUAL' ? (
          <U21Subsection
            collapsed
            collapsible
            shadedHeader
            title="Model Score Details"
          >
            <RiskScoreModelDetails
              latestRiskScore={latestRiskScore}
              riskModelSummary={riskModelSummary}
            />
          </U21Subsection>
        ) : (
          <RiskScoreModelDetails
            latestRiskScore={latestRiskScore}
            riskModelSummary={riskModelSummary}
          />
        )}
      </U21Spacer>
      {showModelRedirect && (
        <StyledU21Button
          disableTitleCase
          endIcon={<IconArrowRight />}
          to={ROUTES_MAP.riskRatingsId.path.replace(
            ':id',
            `${riskModelSummary.id}`,
          )}
        >
          Go to Risk Model
        </StyledU21Button>
      )}
      {!readOnly && hasOverridePermission && (
        <>
          <RiskScoreOverrideModal
            open={overrideModalOpen}
            onClose={() => setOverrideModalOpen(false)}
            latestRiskScore={latestRiskScore}
            riskModelSummary={riskModelSummary}
          />
          {source === 'MANUAL' && (
            <ClearOverrideConfirmModal
              open={clearOverrideModalOpen}
              onClose={() => setClearOverrideModalOpen(false)}
              entityExternalId={latestRiskScore.entity_external_id}
              modelScore={latestRiskScore.override.model_score}
              riskModelMetadata={riskModelSummary}
            />
          )}
        </>
      )}
    </U21Spacer>
  );
};

const StyledEvidenceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledU21Button = styled(U21Button)`
  align-self: flex-end;
  width: fit-content;
`;
